/** @jsxImportSource @emotion/react */
import React, { useCallback, useContext, useState } from "react";
import { css } from "@emotion/react";
import {
  Button,
  Column,
  ContextMenu,
  Row,
  Text,
  TEXT_VARIANTS,
  Icon,
  IconNames,
  ListItem,
  GlobalSearch,
  Feed,
  Flex,
  Sidebar,
  SidebarSection,
  SidebarSectionListItem,
  SidebarSectionContext,
  Scrollable,
} from "../components";
import { COLORS } from "../shared/theme";
import { useMediaQuery } from "react-responsive";
import { RootContext } from "./Root";

const EXTRA_ENTITY_TYPES = {
  PROFILES: "Profiles",
  COLLECTIONS: "Collections",
  PEOPLE: "People",
};

const ENTITY_TYPES = {
  EVERYTHING: "Everything",
  ARTICLES: "Articles",
  AUDIO: "Audio",
  BOOKS: "Books",
  HIGHLIGHTS: "Highlights",
  PICTURES: "Pictures",
  TEXT: "Texts",
  TWEETS: "Tweets",
  VIDEO: "Videos",
  WEBSITES: "Websites",
} as const;

const ICON_MAP = {
  PROFILES: IconNames.Curator,
  COLLECTIONS: IconNames.Collection,
  PEOPLE: IconNames.People,
  EVERYTHING: IconNames.Everything,
  ARTICLES: IconNames.Articles,
  AUDIO: IconNames.Listen,
  BOOKS: IconNames.Books,
  HIGHLIGHTS: IconNames.Highlights,
  PICTURES: IconNames.Pictures,
  TEXT: IconNames.Texts,
  TWEETS: IconNames.Tweets,
  VIDEO: IconNames.Videos,
  WEBSITES: IconNames.Websites,
} as const;

const Pill: React.FC<{
  label: string;
  iconName: string;
  onClick: () => void;
}> = ({ label, iconName, onClick }) => (
  <Button
    variant="solid"
    label={label}
    textVariant={TEXT_VARIANTS.T20}
    textColor={COLORS.BLACK}
    leadingElement={() => <Icon name={iconName} />}
    trailingElement={() => <Icon name={IconNames.X} />}
    onClick={onClick}
  />
);

const SublimeSidebar: React.FC<{
  value: Array<[string, Array<string>]>;
  onChange: (value: Array<[string, Array<string>]>) => void;
}> = ({ value, onChange }) => {
  const [query, setQuery] = useState("");
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [isEntitiesSectionExpanded, setIsEntitiesSectionExpanded] =
    useState(false);
  const isMobile = useMediaQuery({ maxWidth: 700 });

  return (
    <Column gap={isMobile ? 5 : 25}>
      <GlobalSearch
        onEnter={() => setIsSearchActive(true)}
        value={query}
        onChange={setQuery}
        onEsc={() => {
          setIsSearchActive(false);
          onChange([]);
        }}
      />
      {isMobile ? (
        <Row
          role="button"
          mainAxisAlignment="space-between"
          crossAxisAlignment="center"
          css={css`
            height: 35px;
            padding: 0 5px;
            border-radius: 10px;
            background-color: ${COLORS.LIGHT};
          `}
        >
          <Row gap={5} crossAxisAlignment="center">
            <Icon name={IconNames.Following} />
            <Text variant={TEXT_VARIANTS.T20}>{"Following"}</Text>
          </Row>
          <Icon
            name={IconNames.Expand}
            css={css`
              fill: ${COLORS.SILVER};
            `}
          />
        </Row>
      ) : (
        <Sidebar value={value} onChange={onChange}>
          {isSearchActive ? (
            <Column gap={8}>
              <SidebarSection id="entities" multi>
                <SidebarSectionListItem
                  id="PROFILES"
                  icon={<Icon name={IconNames.Curator} />}
                  label="Profiles"
                />
                <SidebarSectionListItem
                  id="COLLECTIONS"
                  icon={<Icon name={IconNames.Collection} />}
                  label="Collections"
                />
                <SidebarSectionListItem
                  id="PEOPLE"
                  icon={<Icon name={IconNames.People} />}
                  label="People"
                />
                <Row
                  role="button"
                  gap={5}
                  crossAxisAlignment="center"
                  css={css`
                    cursor: pointer;
                    height: 35px;
                  `}
                  onClick={() =>
                    setIsEntitiesSectionExpanded(!isEntitiesSectionExpanded)
                  }
                >
                  <Icon
                    name={IconNames.Expand}
                    css={css`
                      transition: all 120ms ease;
                      transform: rotate(
                        ${!isEntitiesSectionExpanded ? 180 : 0}deg
                      );
                      fill: ${COLORS.SILVER};
                    `}
                  />
                  <Text variant={TEXT_VARIANTS.T20}>{"Content"}</Text>
                </Row>
                {isEntitiesSectionExpanded && (
                  <Column
                    gap={5}
                    css={css`
                      padding-left: 20px;
                    `}
                  >
                    {Object.keys(ENTITY_TYPES)
                      .filter((type) => type !== "EVERYTHING")
                      .map((type) => (
                        <SidebarSectionListItem
                          id={type}
                          key={type}
                          icon={
                            <Icon
                              name={ICON_MAP[type as keyof typeof ICON_MAP]}
                            />
                          }
                          label={
                            ENTITY_TYPES[type as keyof typeof ENTITY_TYPES]
                          }
                        />
                      ))}
                  </Column>
                )}
              </SidebarSection>
            </Column>
          ) : (
            <>
              <SidebarSection id="feeds">
                <SidebarSectionContext.Consumer>
                  {({ selected, updateSelected }) => {
                    if (selected.includes("FOLLOWING") && selected.length > 1)
                      updateSelected(
                        selected.filter((id) => id !== "FOLLOWING")
                      );
                    if (selected.length === 0) updateSelected(["FOLLOWING"]);

                    return (
                      <>
                        <SidebarSectionListItem
                          id="FOLLOWING"
                          icon={<Icon name={IconNames.Following} />}
                          label="Following"
                          onClick={(isActive) => {
                            if (!isActive) updateSelected(["FOLLOWING"]);
                          }}
                        />
                      </>
                    );
                  }}
                </SidebarSectionContext.Consumer>
                <SidebarSectionListItem
                  id="EXPLORE"
                  icon={<Icon name={IconNames.Explore} />}
                  label="Explore"
                />
                <SidebarSectionListItem
                  id="STAFF-PICKS"
                  icon={<Icon name={IconNames.StaffPicks} />}
                  label="Staff picks"
                />
              </SidebarSection>
              <SidebarSection id="entities" multi>
                <SidebarSectionContext.Consumer>
                  {({ selected, updateSelected }) => {
                    if (selected.includes("EVERYTHING") && selected.length > 1)
                      updateSelected(
                        selected.filter((id) => id !== "EVERYTHING")
                      );
                    if (selected.length === 0) updateSelected(["EVERYTHING"]);

                    return (
                      <>
                        <SidebarSectionListItem
                          id="EVERYTHING"
                          icon={<Icon name={IconNames.Everything} />}
                          label="Everything"
                          onClick={(isActive) => {
                            if (!isActive) updateSelected(["EVERYTHING"]);
                          }}
                        />
                      </>
                    );
                  }}
                </SidebarSectionContext.Consumer>
                {Object.keys(ENTITY_TYPES)
                  .filter((type) => type !== "EVERYTHING")
                  .map((type) => (
                    <SidebarSectionListItem
                      id={type}
                      key={type}
                      icon={
                        <Icon name={ICON_MAP[type as keyof typeof ICON_MAP]} />
                      }
                      label={ENTITY_TYPES[type as keyof typeof ENTITY_TYPES]}
                    />
                  ))}
              </SidebarSection>
            </>
          )}
        </Sidebar>
      )}
    </Column>
  );
};

const Sublime: React.FC = () => {
  const [value, setValue] = useState<Array<[string, Array<string>]>>([]);
  const isMobile = useMediaQuery({ maxWidth: 700 });
  const { isNavigationExpanded, sidebarGap } = useContext(RootContext);

  const getSection = useCallback(
    (sectionName: string) =>
      value.filter((section) => section[0] === sectionName),
    [value]
  );

  const removeFromSection = useCallback(
    (sectionName: string, key: string) => {
      const sectionIndex = value.findIndex(
        (section) => section[0] === sectionName
      );

      if (sectionIndex === -1) return;

      const updatedSection = value[sectionIndex][1].filter(
        (sectionItem) => sectionItem !== key
      );

      const result = value.slice();
      result[sectionIndex] = [value[sectionIndex][0], updatedSection];

      setValue(result);
    },
    [value]
  );

  return (
    <Flex
      direction={isMobile ? "column" : "row"}
      gap={isMobile ? 25 : 100}
      css={css`
        width: 1266px;
        max-width: calc(100% - ${isMobile ? 20 : 100}px);
      `}
    >
      <div
        css={css`
          width: ${isMobile ? 100 : 40}%;
        `}
      >
        {isMobile ? (
          <SublimeSidebar value={value} onChange={setValue} />
        ) : (
          <div
            css={css`
              transition: all 120ms ease;
              position: sticky;
              top: ${isNavigationExpanded ? 55 + sidebarGap : sidebarGap}px;
              height: fit-content;
            `}
          >
            <Scrollable height="calc(100vh - 125px)">
              <SublimeSidebar value={value} onChange={setValue} />
            </Scrollable>
          </div>
        )}
      </div>
      <Column
        as="main"
        gap={25}
        css={css`
          width: ${isMobile ? 100 : 60}%;
        `}
      >
        {!isMobile && (
          <Row mainAxisAlignment="space-between">
            <Row
              css={css`
                width: calc(100% - 200px);
                flex-wrap: wrap;
                row-gap: 10px;
                column-gap: 5px;
              `}
            >
              {getSection("entities").map((section) =>
                section[1]
                  .filter((value) => value !== "EVERYTHING")
                  .map((value) => (
                    <Pill
                      key={value}
                      label={
                        ENTITY_TYPES[value as keyof typeof ENTITY_TYPES] ||
                        EXTRA_ENTITY_TYPES[
                          value as keyof typeof EXTRA_ENTITY_TYPES
                        ]
                      }
                      iconName={ICON_MAP[value as keyof typeof ICON_MAP]}
                      onClick={() => removeFromSection("entities", value)}
                    />
                  ))
              )}
            </Row>
            <ContextMenu
              renderElement={(toggleOpen) => (
                <Button
                  label="Most recent"
                  trailingElement={() => (
                    <Icon name={IconNames.Sort} size={25} />
                  )}
                  onClick={toggleOpen}
                  textVariant={TEXT_VARIANTS.T20}
                />
              )}
              renderMenu={() => (
                <Column
                  gap={5}
                  css={css`
                    width: 250px;
                    padding: 5px;
                  `}
                >
                  <Row
                    crossAxisAlignment="center"
                    css={css`
                      height: 35px;
                      padding: 0 10px;
                    `}
                  >
                    <Text
                      variant={TEXT_VARIANTS.T15CAPS}
                      css={css`
                        color: ${COLORS.SILVER};
                      `}
                    >
                      {"SORT FEED BY"}
                    </Text>
                  </Row>
                  {[
                    "Most recent",
                    "Most relevant",
                    "Most connected",
                    "Random",
                  ].map((label) => (
                    <ListItem label={label} />
                  ))}
                </Column>
              )}
            />
          </Row>
        )}
        <Feed />
      </Column>
    </Flex>
  );
};

export default Sublime;
